import type {
  Scenario,
  InventoryScopeRule,
  InventoryScopeRuleEntry,
  TransferUpload,
} from '@/types';
import { api } from './api';

// The inventory's EF-scenarios — the source for the disclosure's
// scenario-selector row AND the Scenarios management page. A scenario is the
// ledger-build *input* a disclosure pins: its name, its EF libraries, and its
// scope rules. `is_default` marks the auto-created fallback (never
// deletable). The same shape as `src/api/places.ts`, grown with the management
// CRUD + the scope-rule surface village exposes (`#243`). (The
// decarbonisation-pathway concept is the separate `pathway-scenarios` api.)
//
// Scope rules are managed granularly, matching village's API: `declare` is
// idempotent and returns the full list; `delete` removes one row by id. So
// there is no batched save — each rule change is its own mutation that
// invalidates the `ScopeRules` tag.
const scenariosApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getScenarios: builder.query<Scenario[], number>({
      query: (inventoryId) => `inventories/${inventoryId}/scenarios`,
      providesTags: ['Scenarios'],
    }),
    getScenario: builder.query<Scenario, number>({
      query: (scenarioId) => `scenarios/${scenarioId}`,
      providesTags: ['Scenarios'],
    }),
    createScenario: builder.mutation<
      Scenario,
      { inventoryId: number; name: string }
    >({
      query: ({ inventoryId, name }) => ({
        url: `inventories/${inventoryId}/scenarios`,
        method: 'POST',
        body: { name },
      }),
      invalidatesTags: ['Scenarios'],
    }),
    // The scenario's scalar edits (name + horizon year + reporting period)
    // applied as one atomic update — the editor's Save sends every field
    // together (village clears omitted fields). The horizon bounds the
    // ledger build and the reporting period drives the chart's boundary +
    // complete/provisional split, so invalidate `Emissions` too (the
    // inventory chart must refetch when either moves). `horizonYear` is a
    // reporting year or null ("earliest data point"); `reportingEndMonth` is 1–12
    // or null (calendar years); `lastReviewedYear` is the last settled
    // reporting year or null (nothing reviewed).
    updateScenario: builder.mutation<
      Scenario,
      {
        scenarioId: number;
        name: string;
        horizonYear: number | null;
        reportingEndMonth: number | null;
        lastReviewedYear: number | null;
      }
    >({
      query: ({
        scenarioId,
        name,
        horizonYear,
        reportingEndMonth,
        lastReviewedYear,
      }) => ({
        url: `scenarios/${scenarioId}`,
        method: 'POST',
        body: { name, horizonYear, reportingEndMonth, lastReviewedYear },
      }),
      invalidatesTags: ['Scenarios', 'Emissions'],
    }),
    deleteScenario: builder.mutation<void, { scenarioId: number }>({
      query: ({ scenarioId }) => ({
        url: `scenarios/${scenarioId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Scenarios'],
    }),
    getScopeRules: builder.query<InventoryScopeRuleEntry[], number>({
      query: (scenarioId) => `scenarios/${scenarioId}/scope-rules`,
      providesTags: ['ScopeRules'],
    }),
    declareScopeRule: builder.mutation<
      InventoryScopeRuleEntry[],
      { scenarioId: number; specifier: InventoryScopeRule }
    >({
      query: ({ scenarioId, specifier }) => ({
        url: `scenarios/${scenarioId}/scope-rules`,
        method: 'POST',
        body: specifier,
      }),
      invalidatesTags: ['ScopeRules'],
    }),
    deleteScopeRule: builder.mutation<void, { ruleId: number }>({
      query: ({ ruleId }) => ({
        url: `scope-rules/${ruleId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ScopeRules'],
    }),
    // The methodology reference — the scenario's single, replaceable PDF.
    // Upload is the standard transfer flow: mint an upload (presigned URL +
    // bare key), PUT the bytes from the browser, then bind the key here.
    createScenarioUpload: builder.mutation<
      TransferUpload,
      { scenarioId: number; format: string }
    >({
      query: ({ scenarioId, format }) => ({
        url: `scenarios/${scenarioId}/uploads`,
        method: 'POST',
        body: { format },
      }),
    }),
    setScenarioMethodology: builder.mutation<
      Scenario,
      { scenarioId: number; key: string; filename: string; format: string }
    >({
      query: ({ scenarioId, key, filename, format }) => ({
        url: `scenarios/${scenarioId}/methodology`,
        method: 'POST',
        body: { key, filename, format },
      }),
      invalidatesTags: ['Scenarios'],
    }),
    removeScenarioMethodology: builder.mutation<void, { scenarioId: number }>({
      query: ({ scenarioId }) => ({
        url: `scenarios/${scenarioId}/methodology`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Scenarios'],
    }),
    // Same shape as `downloadEvidence`: typed `void`, the signed URL the
    // server returns is followed in `onQueryStarted`. The locked
    // transfer-download pattern. Keyed on the scenario id — the disclosure
    // methodology page passes its document's effective scenario id.
    downloadScenarioMethodology: builder.mutation<void, { scenarioId: number }>(
      {
        query: ({ scenarioId }) => ({
          url: `scenarios/${scenarioId}/methodology/download`,
          method: 'GET',
        }),
        onQueryStarted: async (_, { queryFulfilled }) => {
          queryFulfilled.then(({ data }: any) => {
            window.location = data.url;
          });
        },
      },
    ),
  }),
});

export const {
  useGetScenariosQuery,
  useGetScenarioQuery,
  useCreateScenarioMutation,
  useUpdateScenarioMutation,
  useDeleteScenarioMutation,
  useGetScopeRulesQuery,
  useDeclareScopeRuleMutation,
  useDeleteScopeRuleMutation,
  useCreateScenarioUploadMutation,
  useSetScenarioMethodologyMutation,
  useRemoveScenarioMethodologyMutation,
  useDownloadScenarioMethodologyMutation,
} = scenariosApi;
